/**
 * MediaPipe Face Landmarker integration (Tasks Vision API)
 * Migrated from deprecated @mediapipe/face_mesh to @mediapipe/tasks-vision
 */

import { FaceLandmarker, FilesetResolver, FaceLandmarkerResult } from '@mediapipe/tasks-vision';
import { BBox } from '../types';

export interface FaceLandmarks {
  landmarks: number[][]; // [[x,y,z], ...]
  bbox: BBox;
  confidence: number;
}

// Fallback WASM CDN URLs in case primary CDN is unavailable
const WASM_BASES = [
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.7/wasm',
  'https://unpkg.com/@mediapipe/tasks-vision@0.10.7/wasm',
];

// Fallback model URLs with multiple sources
const MODEL_URLS = [
  'https://storage.googleapis.com/mediapipe-assets/face_landmarker.task',
  'https://storage.googleapis.com/mediapipe-assets/face_landmarker_v2.task',
];

export class MediaPipeFaceDetector {
  private faceLandmarker: FaceLandmarker | null = null;
  private isInitialized: boolean = false;
  private lastVideoTime: number = -1;

  constructor() {
    // FaceLandmarker is created during initialize()
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    let filesetResolver = null;
    let wasmError: Error | null = null;

    // Try each WASM base URL until one works
    for (const wasmBase of WASM_BASES) {
      try {
        filesetResolver = await FilesetResolver.forVisionTasks(wasmBase);
        console.log('MediaPipe WASM loaded from:', wasmBase);
        break;
      } catch (e) {
        wasmError = e as Error;
        console.warn('WASM load failed, trying next CDN:', wasmBase, e);
      }
    }

    if (!filesetResolver) {
      throw new Error(
        `Failed to load MediaPipe WASM from all CDNs. Last error: ${wasmError?.message}`
      );
    }

    let lastError: Error | null = null;
    for (const modelUrl of MODEL_URLS) {
      try {
        this.faceLandmarker = await FaceLandmarker.createFromOptions(filesetResolver, {
          baseOptions: { modelAssetPath: modelUrl },
          outputFaceBlendshapes: false,
          outputFacialTransformationMatrixes: false,
          runningMode: 'VIDEO',
          numFaces: 1,
        });
        console.log('MediaPipe FaceLandmarker loaded from:', modelUrl);
        break;
      } catch (e) {
        lastError = e as Error;
        console.warn('Model load failed, trying next:', modelUrl, e);
      }
    }

    if (!this.faceLandmarker) {
      throw new Error(
        `Failed to load FaceLandmarker from all sources. Last error: ${lastError?.message}`
      );
    }

    this.isInitialized = true;
  }

  /**
   * Detect face in video frame and return landmarks + bbox
   */
  async detectFace(video: HTMLVideoElement): Promise<FaceLandmarks | null> {
    if (!this.isInitialized || !this.faceLandmarker) {
      await this.initialize();
    }

    // Skip if video isn't ready
    if (video.readyState < 2) {
      return null;
    }

    // Avoid processing the same frame twice (for video playback)
    // NOTE: For camera streams, we skip this check since currentTime may not update reliably
    const currentTime = video.currentTime;
    const isLiveStream = video.srcObject instanceof MediaStream;
    if (!isLiveStream && currentTime === this.lastVideoTime) {
      return null;
    }
    this.lastVideoTime = currentTime;

    try {
      const result: FaceLandmarkerResult = this.faceLandmarker!.detectForVideo(
        video,
        performance.now()
      );

      if (!result.faceLandmarks || result.faceLandmarks.length === 0) {
        return null;
      }

      const landmarks = result.faceLandmarks[0];
      const normalizedLandmarks = landmarks.map((lm) => [lm.x, lm.y, lm.z || 0]);

      // Compute bounding box from landmarks
      const xs = normalizedLandmarks.map((lm) => lm[0]);
      const ys = normalizedLandmarks.map((lm) => lm[1]);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const bbox: BBox = {
        x: minX * video.videoWidth,
        y: minY * video.videoHeight,
        w: (maxX - minX) * video.videoWidth,
        h: (maxY - minY) * video.videoHeight,
      };

      return {
        landmarks: normalizedLandmarks,
        bbox,
        confidence: 0.95, // FaceLandmarker doesn't expose per-detection confidence directly
      };
    } catch (e) {
      console.error('Face detection error:', e);
      return null;
    }
  }

  /**
   * @deprecated Camera utility removed - use native getUserMedia + requestAnimationFrame
   * This method is kept for API compatibility but returns a simple object
   */
  createCamera(
    videoElement: HTMLVideoElement,
    options?: { width?: number; height?: number }
  ): { start: () => Promise<void>; stop: () => void } {
    console.warn(
      'createCamera() is deprecated. Use navigator.mediaDevices.getUserMedia() directly.'
    );
    return {
      start: async () => {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: 'user',
            width: options?.width || 640,
            height: options?.height || 480,
          },
        });
        videoElement.srcObject = stream;
        await videoElement.play();
      },
      stop: () => {
        const stream = videoElement.srcObject as MediaStream | null;
        stream?.getTracks().forEach((track) => track.stop());
        videoElement.srcObject = null;
      },
    };
  }

  dispose(): void {
    if (this.faceLandmarker) {
      this.faceLandmarker.close();
      this.faceLandmarker = null;
    }
    this.isInitialized = false;
  }
}
